import { Box, Button, Collapse, Link, Paper, Stack, Typography } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from './helpers/Spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { NoEventData } from './helpers/NoEventData';
import DescriptionIcon from '@mui/icons-material/Description';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FolderIcon from '@mui/icons-material/Folder';

// Define TypeScript types for your data
interface Discipline {
    name: string;
    listDisplayName?: string;
    hidden?: boolean;
    startList?: string;
    results?: string;
}

interface EventDay {
    day: number;
    date: string;
    events: Discipline[];
}

interface EventData {
    name: string;
    date: string;
    schedule?: string;
    disciplines: EventDay[];
    pdfs?: PdfDocument[];
}

interface PdfDocument {
    name: string;
    url: string;
}

const isToday = (dateString: string) => {
    const [day, month, year] = dateString.split('/').map(Number);

    const today = new Date();

    return day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
};

export const EventDetail: FC = () => {
    const { eventName } = useParams<{ eventName: string }>();
    const navigate = useNavigate();
    const [eventData, setEventData] = useState<EventData | null>(null);
    const [openDay, setOpenDay] = useState<number | null>(0);
    const [isLoading, setIsLoading] = useState(true);

    const toggleDay = (index: number) => {
        setOpenDay((prev) => (prev === index ? null : index));
    };

    // ✅ Load event data from backend based on eventName
    useEffect(() => {
        if (!eventName) return;

        setIsLoading(true);

        fetch(`/data/${eventName}.json`)
            .then((response) => response.json())
            .then((data: EventData) => {
                setEventData(data);
                data.disciplines.forEach((discipline, index) => {
                    if (isToday(discipline.date)) {
                        setOpenDay(index);
                    }
                });
            })
            .finally(() => setIsLoading(false))
            .catch((error) => console.error('❌ Error loading event data:', error));
    }, [eventName]);

    if (isLoading) {
        return <Spinner />;
    }

    if (!eventData) {
        return <NoEventData />;
    }

    return (
        <Box sx={{ width: '85%', mx: 'auto', mt: 4 }}>
            {/* 🔙 Back Button */}
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
                Back to Events
            </Button>

            {/* 🏁 Event Header */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <Typography variant="h4" fontWeight={700}>
                    {eventData.name}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {eventData.date}
                </Typography>

                {eventData.schedule && (
                    <Link
                        href={eventData.schedule}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                    >
                        <DescriptionIcon fontSize="small" />
                        Event Schedule
                    </Link>
                )}

                {eventData?.pdfs?.[0] && (
                    <Link
                        href={eventData?.pdfs?.[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, paddingLeft: 2 }}
                    >
                        <EmojiEventsIcon fontSize="small" />
                        Results
                    </Link>
                )}
            </Paper>

            {/* 📅 Days */}
            {eventData.disciplines.map((day, dayIndex) => (
                <Paper
                    key={dayIndex}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: 2,
                        overflow: 'hidden',
                    }}
                >
                    {/* Day Header */}
                    <Box
                        onClick={() => toggleDay(dayIndex)}
                        sx={{
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            backgroundColor: 'grey.900',
                            color: 'common.white',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography fontWeight={600}>
                            Day {day.day} - {day.date}
                        </Typography>

                        <Typography>{openDay === dayIndex ? '−' : '+'}</Typography>
                    </Box>

                    {/* Collapsible Content */}
                    <Collapse in={openDay === dayIndex}>
                        <Box sx={{ p: 2 }}>
                            <Stack spacing={1.5}>
                                {day.events
                                    .filter((event) => !event.hidden) // only visible events
                                    .map((event, index, arr) => (
                                        <Box
                                            key={event.name} // use a stable key
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                pb: 1,
                                                borderBottom: index === arr.length - 1 ? 'none' : '1px solid',
                                                borderColor: '#1f2937',
                                            }}
                                        >
                                            {/* Discipline Name */}
                                            <Typography
                                                sx={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    fontWeight: 500,
                                                }}
                                                onClick={() =>
                                                    navigate(
                                                        `/event/${eventName}/discipline/${event.name
                                                            .replace(/\s+/g, '-')
                                                            .toLowerCase()}`,
                                                    )
                                                }
                                            >
                                                {event.listDisplayName || event.name}
                                            </Typography>

                                            {/* Links */}
                                            <Stack direction="row" spacing={2}>
                                                {event.startList && (
                                                    <Link
                                                        href={event.startList}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                                    >
                                                        <FolderIcon fontSize="small" />
                                                        StartList
                                                    </Link>
                                                )}

                                                {event.results && (
                                                    <Link
                                                        href={event.results}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                                    >
                                                        <EmojiEventsIcon fontSize="small" />
                                                        Results
                                                    </Link>
                                                )}
                                            </Stack>
                                        </Box>
                                    ))}
                            </Stack>
                        </Box>
                    </Collapse>
                </Paper>
            ))}
        </Box>
    );
};
